// React
import { useState, useCallback, useMemo, useRef } from 'react';

// Styled
import {
  ActionItemIcon,
  FileItemContainer,
  LoadingFileItemContainer,
  UploadFileButton,
} from './style';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useDropzone } from 'react-dropzone';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  downloadFile,
  getFileExtensionType,
  getFileName,
  getUrlExtension,
} from 'utils/globalFunction';
import mime from 'mime';
import { useDispatch } from 'react-redux';
import actions from 'store/actions';
import { getFileTypeFromExt } from 'xeniautils';
import CancelIcon from '@mui/icons-material/Cancel';
import { FILE_TYPES } from './utils';
import CustomButton from 'components/Button/CustomButton';
import DownloadIcon from 'components/Icons/downloadIcon';
import useUpload from 'utils/CustomHooks/useUpload';
import { v4 as uuidV4 } from 'uuid';
import useUploadPublic from 'utils/CustomHooks/useUploadPublic';
import { handleVideoThumbnail } from 'utils/thumbnailGeneration';

interface FileUploadProps {
  onChangeCb?: (fileUrls: string[], otherProps?: any) => void;
  attachments?: string[];
  thumbnails?: any;
  isPreview?: boolean;
  isShowStatic?: boolean;
  isPublic?: boolean;
  log?: any;
  workspaceId?: string;
}

interface AttachmentData {
  url: string;
  type: string | null;
}

const UploadFile: React.FC<FileUploadProps> = ({
  onChangeCb,
  attachments = [],
  thumbnails = {},
  isPreview = false,
  isShowStatic = false,
  isPublic = false,
  log,
  workspaceId,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState<string[]>(attachments);
  const [thumbnailsList, setThumbnailsList] = useState<any>(thumbnails ?? {});
  const cancelUploadSignalRef = useRef<boolean>(false);

  const { uploadFile } = useUpload();
  const { uploadFilePublic } = useUploadPublic();

  const generateOtherProps = (fileUrl: string, thumbnailResponse: any) => {
    let otherProps: any = undefined;
    if (thumbnailResponse) {
      const thumbnailUrl =
        thumbnailResponse?.baseUrl + thumbnailResponse?.filePath?.[0];

      otherProps = {
        thumbnails: {
          [fileUrl]: thumbnailUrl,
          ...thumbnailsList,
        },
      };
    }

    return otherProps;
  };

  const generateVideoThumbnail = async (fileUrl: string) => {
    const extension = getUrlExtension(fileUrl);
    const type = getFileTypeFromExt(extension);

    if (type === 'video') {
      const thumbnailResponse = await handleVideoThumbnail(
        fileUrl,
        isPublic,
        log,
        workspaceId,
      );

      const otherProps = generateOtherProps(fileUrl, thumbnailResponse);
      return otherProps;
    }

    return {
      thumbnails: thumbnailsList,
    };
  };

  const uploadFileToServer = useCallback(
    async (acceptedFiles: File[]) => {
      setIsUploading(true);
      try {
        if (isPublic) {
          uploadFilePublic(
            {
              uploadId: uuidV4(),
              file: acceptedFiles[0],
              isStay: true,
              publicData: {
                isPublic: isPublic,
                log: log,
                workspaceId: workspaceId,
              },
            },
            // this call back is after uploading done
            async (response) => {
              if (cancelUploadSignalRef.current) {
                cancelUploadSignalRef.current = false;
                setIsUploading(false);
                return;
              }

              const path = response?.filePath;
              const baseUrl = response?.baseUrl;
              const fileUrls = [baseUrl + path];

              const otherProps = await generateVideoThumbnail(fileUrls?.[0]);

              const updatedFiles = [
                ...(fileUrls ?? []),
                ...(uploadedFiles ?? []),
              ];

              onChangeCb?.(updatedFiles, otherProps);
              setUploadedFiles(updatedFiles);
              if (otherProps?.thumbnails) {
                setThumbnailsList(otherProps?.thumbnails);
              }
              setIsUploading(false);
            },
          );
        } else {
          uploadFile(
            {
              uploadId: uuidV4(),
              file: acceptedFiles[0],
              isStay: true,
            },
            // this call back is after uploading done
            async (response) => {
              if (cancelUploadSignalRef.current) {
                cancelUploadSignalRef.current = false;
                setIsUploading(false);
                return;
              }

              const path = response?.filePath;
              const baseUrl = response?.baseUrl;
              const fileUrls = [baseUrl + path];

              const otherProps = await generateVideoThumbnail(fileUrls?.[0]);

              const updatedFiles = [
                ...(fileUrls ?? []),
                ...(uploadedFiles ?? []),
              ];

              onChangeCb?.(updatedFiles, otherProps);
              setUploadedFiles(updatedFiles);
              if (otherProps?.thumbnails) {
                setThumbnailsList(otherProps?.thumbnails);
              }
              setIsUploading(false);
            },
          );
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        setIsUploading(false);
      }
    },
    [onChangeCb, uploadedFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    disabled: isUploading,
    maxFiles: 1,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'text/csv': ['.csv'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        ['.pptx'],
      'image/*': ['.jpeg', '.jpg', '.png', '.heic', '.webp'],
      'video/mp4': ['.mp4'],
      'video/webm': ['.webm'],
      'video/x-msvideo': ['.avi'],
      'application/zip': ['.zip'],
    },
    onDropAccepted: uploadFileToServer,
  });

  const attachmentsData = useMemo<AttachmentData[]>(
    () =>
      uploadedFiles.map((attachment) => ({
        url: attachment,
        type: mime.getType(attachment),
      })),
    [uploadedFiles],
  );

  const setAttachmentsPreviewData = useCallback(
    (index: number) => {
      dispatch(
        actions.setAttachmentsPreviewData({
          index,
          attachments: attachmentsData,
        } as any),
      );
    },
    [dispatch, attachmentsData],
  );

  const handleRemoveFile = (index: number) => {
    const fileToDelete = uploadedFiles?.[index];

    const filteredFiles = uploadedFiles?.filter(
      (_, fileIndex) => fileIndex !== index,
    );

    const newThumbnails = thumbnailsList ?? {};
    delete newThumbnails[fileToDelete];

    const otherProps = {
      thumbnails: newThumbnails,
    };

    onChangeCb?.(filteredFiles, otherProps);
    setUploadedFiles(filteredFiles);
    setThumbnailsList(newThumbnails);
  };

  const cancelUpload = () => {
    cancelUploadSignalRef.current = true;
    setIsUploading(false);
  };

  const LoadingItem = () => {
    return (
      <LoadingFileItemContainer>
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={25} />
          <Typography className="UploadingName">Uploading...</Typography>
        </Stack>
        {/* <CancelIcon
          className="cancelIcon"
          onClick={(e) => {
            e.stopPropagation();
            cancelUpload();
          }}
        /> */}
      </LoadingFileItemContainer>
    );
  };

  const downloadHandler = useCallback((event, attachment) => {
    event.stopPropagation();
    try {
      downloadFile(
        attachment,
        `${getFileName(attachment)}.${getUrlExtension(attachment)}`,
      );
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }, []);

  const renderAttachment = useCallback(
    (attachment: string, index: number) => {
      const extension = getUrlExtension(attachment);
      const type = getFileTypeFromExt(extension);
      const fileTypeData = FILE_TYPES[type];

      const FileTypeIcon = fileTypeData?.icon;
      const fileName = attachment.split('/').pop() || '';

      return (
        <FileItemContainer
          sx={{
            ...(isPreview && {
              backgroundColor: 'rgba(240, 243, 244, 1)',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'white',
                '& .downloadBtn': {
                  display: 'flex',
                },
              },
            }),
          }}
          key={attachment + '_' + index}
          onClick={() => setAttachmentsPreviewData(index)}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            {FileTypeIcon && (
              <ActionItemIcon
                bgcolor={fileTypeData.bgColor}
                svgdimensions={fileTypeData.styles}
              >
                <FileTypeIcon />
              </ActionItemIcon>
            )}
            <Typography className="FileName">{fileName}</Typography>
          </Stack>

          {!isPreview && (
            <CancelIcon
              className="cancelIcon"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile(index);
              }}
            />
          )}

          {isPreview && (
            <CustomButton
              className="downloadBtn"
              onClick={(event) => downloadHandler(event, attachment)}
              startIcon={<DownloadIcon />}
            >
              Download
            </CustomButton>
          )}
        </FileItemContainer>
      );
    },
    [setAttachmentsPreviewData],
  );

  return (
    <>
      {!isPreview && !isShowStatic && (
        <Box {...getRootProps()} sx={{ width: '100%' }}>
          <input {...getInputProps()} disabled={isUploading} />
          <UploadFileButton
            variant="outlined"
            startIcon={<CloudUploadOutlinedIcon />}
            disabled={isUploading}
          >
            Upload File
          </UploadFileButton>
        </Box>
      )}

      {isShowStatic && (
        <Box sx={{ width: '100%' }}>
          <UploadFileButton
            variant="outlined"
            startIcon={<CloudUploadOutlinedIcon />}
            disabled={isUploading}
          >
            Upload File
          </UploadFileButton>
        </Box>
      )}

      {isUploading && <LoadingItem />}
      {uploadedFiles.map((file, index) => renderAttachment(file, index))}
    </>
  );
};

export default UploadFile;
